/**
 * The geometry of the paper.
 *
 * Everything here is measuring and calculating, none of it is view. Preact
 * cannot take that over: where a line stands, you only know once the text is
 * laid out, and what the font measures only once it is loaded. Both are
 * readings from the browser, not derivations from the state.
 *
 * The calculation nevertheless stands for itself: `baselineFor()` gets numbers
 * and gives numbers back. Only the edges — the measuring above, the setting of
 * the CSS variables below — touch the DOM.
 */

import type { Paper } from '../types.js'
import { A4, X_HEIGHT_RATIO, X_HEIGHT_RATIO_GRID } from '../ui/paper.js'

/** Millimetres to pixels — for calculations that CSS cannot take over. */
export function mmToPx(mm: number): number {
  return mm * (96 / 25.4)
}

/** Air to the left and right of the sheet, in pixels. */
export const SHEET_GAP = 10

/**
 * SIT_ON_RULE keeps the font one pixel above the rule. If it sat
 * exactly on it, the letters would cover it — the rule would disappear
 * exactly where text stands.
 */
const SIT_ON_RULE = 1

/** What a font needs in height above and below the baseline. */
export interface FontMetrics {
  ascent: number
  descent: number
}

/**
 * Measures the font.
 *
 * Via a canvas, because only `measureText` knows the true metrics of the
 * loaded font — CSS does not give them out.
 */
export function measureFont(sizePx: number, family: string): FontMetrics | null {
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) return null
  ctx.font = `${sizePx}px "${family}", system-ui`
  const m = ctx.measureText('Hxgp')
  if (!m.fontBoundingBoxAscent) return null
  return { ascent: m.fontBoundingBoxAscent, descent: m.fontBoundingBoxDescent }
}

/** What `baselineFor` calculates — three CSS variables. */
export interface Baseline {
  ruleOffset: number
  baseline: number
  textShift: number
}

/**
 * Aligns the paper grid and the font to each other.
 *
 * The order is decisive and used to be the wrong way round: FIRST the grid is
 * fixed, THEN the text follows it. The other way round — the grid derived
 * from the font metrics — made the paper jump at every change of font, and
 * the first row of squares was cut off because the grid started somewhere in
 * the middle of the sheet.
 *
 * The grid now depends only on the paper: it begins one whole square height
 * below the edge of the sheet and runs evenly from there. The font gets its
 * baseline laid so that it sits on these rules.
 */
export function baselineFor(
  p: Paper,
  linePx: number,
  m: FontMetrics
): Baseline | null {
  if (!linePx) return null

  // The type area comes from the paper table, NOT from the element: its
  // padding already carries the text shift that we are only calculating here
  // — that would be circular and the text would wander a bit further on
  // every call.
  const padTop = mmToPx(p.top)
  const raster = mmToPx(p.line)

  // 1. The grid. It sits fixed on the sheet, independent of the font.
  //
  //    The shift is exactly one square height — then the first row is as high
  //    as all the others. Not rounded to pixels: the rounding made the first
  //    row a fraction lower than the rest, and that is exactly what shows at
  //    the edge of the sheet. The browser may distribute the rules themselves
  //    as it likes — rows of unequal height are visible, half a pixel line of
  //    blur is not.
  const rasterStart = raster

  // 2. The font. Its baseline must land on a grid rule.
  //
  // Where the baseline lies within the line box: CSS distributes the
  // difference between line height and font height evenly onto both sides
  // ("half-leading"); the baseline lies below that, shifted by the ascender.
  const inLineBox = (linePx - (m.ascent + m.descent)) / 2 + m.ascent

  // The first text line shall sit on the first grid rule that lies below the
  // type area. `--baseline` is the distance from the type area to there — the
  // text brings the line height itself.
  let target = rasterStart - padTop
  while (target < inLineBox) target += raster

  return {
    ruleOffset: rasterStart,
    baseline: target - SIT_ON_RULE,
    // The shift that the text needs so that its baseline lands exactly there:
    // the difference between the grid rule and the natural line position.
    textShift: target - inLineBox - SIT_ON_RULE,
  }
}

/** Writes the calculated measures to where the CSS reads them. */
export function applyBaseline(b: Baseline): void {
  const css = document.documentElement.style
  css.setProperty('--rule-offset', `${b.ruleOffset.toFixed(3)}px`)
  css.setProperty('--baseline', `${b.baseline.toFixed(3)}px`)
  css.setProperty('--text-shift', `${b.textShift.toFixed(3)}px`)
}

/**
 * Scales the sheet down until it fits into the window.
 *
 * The sheet is true DIN A4 and must stay so: what is called 9 mm has to
 * measure 9 mm on paper. On the phone that does not fit into the screen, so it
 * is scaled down — with `zoom` and not with `transform`, because `transform`
 * shifts the measures for `getBoundingClientRect` but not the text flow.
 *
 * @returns the factor that was set, which the wedge layer needs to calculate
 *          back.
 */
export function fitSheet(stack: HTMLElement, wrap: HTMLElement): number {
  // Measure without a scale, otherwise the last one is measured along.
  stack.style.zoom = ''
  const paper = stack.getBoundingClientRect().width
  const width = wrap.clientWidth
  if (!paper || !width) return 1

  // The padding of the wrapper stays free, so the sheet does not stick to the
  // edge of the display.
  const space = width - 2 * SHEET_GAP
  // Never enlarge: on a wide screen the sheet keeps its true size.
  const scale = Math.min(1, space / paper)
  stack.style.zoom = scale < 1 ? String(scale) : ''
  return scale < 1 ? scale : 1
}

export const fontPx = (el: HTMLElement): number =>
  parseFloat(getComputedStyle(el).fontSize)

export const linePx = (el: HTMLElement): number =>
  parseFloat(getComputedStyle(el).lineHeight)

// --- The font size from the paper -----------------------------------------

/**
 * The x-height of the font, as a fraction of the font size.
 *
 * Measured, not estimated: an own handwriting has different proportions than
 * Atkinson and would otherwise sit too large or too small on the rules.
 */
export function xHeightRatio(family: string): number | null {
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) return null
  const PROBE = 1000
  ctx.font = `${PROBE}px "${family}", monospace`
  const m = ctx.measureText('x')
  // actualBoundingBoxAscent of the small x IS the x-height.
  const xh = m.actualBoundingBoxAscent
  if (!xh || !isFinite(xh)) return null
  return xh / PROBE
}

/** How high the font builds in total, with ascenders and descenders. */
export function totalHeightRatio(family: string): number | null {
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) return null
  const PROBE = 1000
  ctx.font = `${PROBE}px "${family}", monospace`
  const m = ctx.measureText('ÄHgjpqÜ')
  const above = m.actualBoundingBoxAscent
  const below = m.actualBoundingBoxDescent
  if (!above || !isFinite(above)) return null
  return (above + below) / PROBE
}

/** The line spacing: on squared paper with a free row, two squares. */
export function lineFor(p: Paper, gridSpacing: boolean): number {
  return p.kind === 'grid' && gridSpacing ? p.line * 2 : p.line
}

/**
 * The font size in millimetres, calculated from the x-height.
 *
 * With a free row, twice as much space is available, but the font stays as
 * large as the square — after all it shall fit into it.
 */
export function fontSizeFor(
  p: Paper,
  family: string,
  gridSpacing: boolean,
  ratios: { x: number | null; total: number | null } = {
    x: xHeightRatio(family),
    total: totalHeightRatio(family),
  }
): number {
  if (!ratios.x) return p.size

  const ratio = p.kind === 'grid' ? X_HEIGHT_RATIO_GRID : X_HEIGHT_RATIO
  const square = p.kind === 'grid' ? p.line : lineFor(p, gridSpacing)
  const fromXHeight = (square * ratio) / ratios.x

  // On squared paper WITHOUT a free row the font may not build higher than
  // the line.
  //
  // The x-height alone is not enough as a measure: fonts with strong
  // ascenders and descenders — Atkinson, for example, compared to a
  // handwriting — build much higher at the same x-height and then hit the
  // line above and below. Therefore the total height is checked as well and
  // the font is made smaller if need be.
  //
  // With a free row this is unnecessary: there is space for the overhangs.
  if (p.kind === 'grid' && !gridSpacing && ratios.total) {
    const fitsInLine = lineFor(p, gridSpacing) / ratios.total
    return Math.min(fromXHeight, fitsInLine)
  }
  return fromXHeight
}

/**
 * Lays paper and font onto the root element.
 *
 * Deliberately imperative and not in JSX: these variables stand on <html>, so
 * outside the Preact root, and the CSS reads them from there. An effect is
 * more honest here than a detour via an attribute on the body.
 */
export function applyPaper(opts: {
  paper: Paper
  paperId: string
  family: string
  printBg: boolean
  gridSpacing: boolean
}): void {
  const { paper: p, gridSpacing } = opts
  const root = document.documentElement
  root.dataset.paper = opts.paperId
  root.dataset.paperKind = p.kind
  root.dataset.printBg = opts.printBg ? 'on' : 'off'
  root.dataset.gridSpacing = p.kind === 'grid' && gridSpacing ? 'on' : 'off'

  const css = root.style
  // The chosen font. The CSS sets it via --handwriting, so that it applies
  // everywhere — in the field, in the preview and in the printout.
  css.setProperty('--handwriting', `"${opts.family}"`)
  // The size compensation now sits in the measured font size, no longer in a
  // fixed factor — therefore --font-scale stays at 1.
  css.setProperty('--font-scale', '1')
  // The text line spacing — for squared paper with a free row, two squares.
  css.setProperty('--line', `${lineFor(p, gridSpacing)}mm`)
  // The grid stays untouched by this: the squares do not change their size,
  // only the text moves further apart.
  css.setProperty('--cell', `${p.line}mm`)
  css.setProperty('--font-size', `${fontSizeFor(p, opts.family, gridSpacing).toFixed(3)}mm`)
  css.setProperty(
    '--rows',
    String(p.kind === 'grid' && gridSpacing ? Math.floor(p.rows / 2) : p.rows)
  )
  css.setProperty('--pad-top', `${p.top}mm`)
  css.setProperty('--pad-left', `${p.left}mm`)
  css.setProperty('--pad-right', `${p.right}mm`)
  css.setProperty('--text-width', `${p.width ?? A4.w - p.left - p.right}mm`)
  css.setProperty('--page-w', `${A4.w}mm`)
  css.setProperty('--page-h', `${A4.h}mm`)
}
